import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Sun, Moon } from "lucide-react";
import JournalComments from "@/components/JournalComments";

const AUTHORS = {
  cody: "Cody D. Thrash",
  samantha: "Samantha N. Thrash",
};

// Journal essays get a per-reader "reading mode" so members reading long-form
// can flip to a warm cream page for comfort without changing the app's dark
// identity. Choice persists across sessions via localStorage.
const STORAGE_KEY = "journal-reading-mode";

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark"; }
    catch { return "dark"; }
  });

  useEffect(() => {
    api.get(`/blog/${id}`).then(({ data }) => setPost(data)).catch(() => setPost(null));
  }, [id]);

  const toggleMode = () => {
    const next = mode === "light" ? "dark" : "light";
    setMode(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  };

  if (!post) return <div className="max-w-3xl mx-auto px-6 py-32" style={{ color: "var(--muted)" }}>Loading…</div>;

  return (
    <article
      data-testid="blog-post-page"
      data-reading={mode === "light" ? "light" : undefined}
      className="max-w-4xl mx-auto px-6 lg:px-12 py-8 md:py-16"
    >
      <div className="flex items-center justify-between mb-12 gap-4 flex-wrap">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ink-2)" }}>
          <ArrowLeft size={14} /> Back to journal
        </Link>
        <button
          onClick={toggleMode}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.18em] rounded-full transition-colors"
          style={{ color: "var(--ink-2)", border: "1px solid var(--border-subtle)" }}
          data-testid="journal-reading-mode-toggle"
          aria-label={mode === "light" ? "Switch to dark reading mode" : "Switch to light reading mode"}
          title={mode === "light" ? "Comfortable for reading in the day. Tap for dark." : "Easier on evening eyes. Tap for cream."}
        >
          {mode === "light" ? <Moon size={13} /> : <Sun size={13} />}
          <span>{mode === "light" ? "Dark" : "Light"} reading</span>
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6 text-xs uppercase tracking-[0.2em]">
        <span style={{ color: "var(--ember)" }}>{AUTHORS[post.author] || post.author}</span>
        <span className="w-8 h-px" style={{ background: "var(--border-strong)" }} />
        <span style={{ color: "var(--muted)" }}>
          {new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
        </span>
      </div>

      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl tracking-[-0.03em] leading-[0.95]" style={{ color: "var(--ink)" }}>
        {post.title}
      </h1>

      <p className="mt-6 font-serif-display-italic text-xl lg:text-2xl leading-snug" style={{ color: "var(--ink-2)" }}>
        {post.excerpt}
      </p>

      {post.cover_url && (
        <div className="mt-12 aspect-[16/9] overflow-hidden">
          <img src={post.cover_url} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="mt-12 max-w-2xl text-lg leading-[1.75]" style={{ color: "var(--ink-2)" }}>
        {post.body_html ? (
          <div
            className="prose-journal"
            data-testid="journal-post-body"
            dangerouslySetInnerHTML={{ __html: post.body_html }}
          />
        ) : (
          <div className="whitespace-pre-line">{post.body}</div>
        )}
      </div>

      <div className="editorial-rule mt-16" />
      <div className="mt-8 text-sm" style={{ color: "var(--muted)" }}>
        Written by <strong style={{ color: "var(--ink)" }}>{AUTHORS[post.author] || post.author}</strong> for The Disciple Life.
      </div>

      <JournalComments postId={post.id} />
    </article>
  );
}
